import Repository from '@/api/Repository';
import type {
  LiaisonState,
  HistoryItem,
  TemplateData,
  TemplateType,
  NotificationType,
  TransitionAction,
  SendNotificationResponse,
  PreviewResponse,
  LiaisonTarget,
} from '../model/types';

const resource = 'api/liaison';

export default {
  listTargets() {
    return Repository.get<{ items: LiaisonTarget[] }>(`${resource}/targets`);
  },

  getState({ basinId, stationId }: { basinId: string; stationId: string }) {
    return Repository.get<LiaisonState>(
      `${resource}/state/${basinId}/${stationId}`,
    );
  },

  updateState(
    { basinId, stationId }: { basinId: string; stationId: string },
    payload: { action: TransitionAction; emergencyReleaseTime?: string },
  ) {
    return Repository.put<LiaisonState>(
      `${resource}/state/${basinId}/${stationId}`,
      payload,
    );
  },

  send(
    { basinId, stationId }: { basinId: string; stationId: string },
    payload: { type: NotificationType; body: string },
  ) {
    return Repository.post<SendNotificationResponse>(
      `${resource}/send/${basinId}/${stationId}`,
      payload,
    );
  },

  listHistory(
    { basinId, stationId }: { basinId: string; stationId: string },
    params: { fromIso?: string; toIso?: string } = {},
  ) {
    const query = new URLSearchParams();
    if (params.fromIso) query.set('fromIso', params.fromIso);
    if (params.toIso) query.set('toIso', params.toIso);
    const qs = query.toString();
    return Repository.get<{ items: HistoryItem[]; nextCursor: string | null }>(
      `${resource}/history/${basinId}/${stationId}${qs ? `?${qs}` : ''}`,
    );
  },

  getHistoryItem({
    basinId,
    stationId,
    historyId,
  }: {
    basinId: string;
    stationId: string;
    historyId: string;
  }) {
    return Repository.get<HistoryItem>(
      `${resource}/history/${basinId}/${stationId}/${historyId}`,
    );
  },

  getTemplate({
    basinId,
    templateType,
  }: {
    basinId: string;
    templateType: TemplateType;
  }) {
    return Repository.get<TemplateData>(
      `${resource}/template/${basinId}/${templateType}`,
    );
  },

  updateTemplate(
    { basinId, templateType }: { basinId: string; templateType: TemplateType },
    payload: { subject: string; body: string },
  ) {
    return Repository.put<TemplateData>(
      `${resource}/template/${basinId}/${templateType}`,
      payload,
    );
  },

  getPreview({
    basinId,
    stationId,
    type,
  }: {
    basinId: string;
    stationId: string;
    type: 'statusReport' | 'dispatchRequest';
  }) {
    return Repository.get<PreviewResponse>(
      `${resource}/preview/${basinId}/${stationId}?type=${type}`,
    );
  },
};
